import React, { useEffect, useState } from 'react';
import MasterLayout from '../masterLayout/MasterLayout';
import { Form, Button, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import swal from 'sweetalert';
import { Icon } from '@iconify/react';
import { API_URL } from '..';
import { useParams, useNavigate } from 'react-router-dom';

const EditExpenseLogPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    payment_method: '',
    on_date: '',
    attachment: null, // For new file
    existingAttachment: null, // For existing attachment URL
  });
  const [errors, setErrors] = useState({});
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (!storedUser) {
      window.location.href = '/sign-in';
    } else {
      setUser(storedUser);
      fetchExpense(storedUser.id);
    }
  }, []);

  const fetchExpense = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/expense/show/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
        },
      });
      if (response.status === 200) {
        const expense = response.data.data;
        setFormData({
          title: expense.title || '',
          description: expense.description || '',
          amount: expense.amount || '',
          payment_method: expense.payment_method || '',
          on_date: expense.on_date ? expense.on_date.split('T')[0] : '',
          attachment: null,
          existingAttachment: expense.attachment || null,
        });
      } else {
        swal('Error', response.data.message || 'Failed to fetch expense', 'error');
      }
    } catch (error) {
      swal('Error', 'Failed to fetch expense: ' + error.message, 'error');
    }
  };

  const paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'card', label: 'Card' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, attachment: file });
    setErrors({ ...errors, attachment: '' });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.amount || formData.amount <= 0) newErrors.amount = 'Valid amount is required';
    if (!formData.payment_method) newErrors.payment_method = 'Payment method is required';
    if (!formData.on_date) newErrors.on_date = 'Date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const data = new FormData();
    data.append('title', formData.title);
    data.append('description', formData.description);
    data.append('amount', formData.amount);
    data.append('payment_method', formData.payment_method);
    data.append('on_date', formData.on_date);
    if (formData.attachment) {
      data.append('attachment', formData.attachment);
    }

    try {
      const response = await axios.put(`${API_URL}/api/expense/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: '*/*',
        },
      });

      if (response.status === 200) {
        swal('Success', 'Expense updated successfully', 'success');
        navigate('/expense'); // Redirect to expenses list
      } else {
        swal('Error', response.data.message || 'Failed to update expense', 'error');
      }
    } catch (error) {
      swal('Error', 'Failed to update expense: ' + error.message, 'error');
    }
  };

  return (
    <MasterLayout>
      <div className="container py-5">
        <Row className="justify-content-center">
          <Col lg={8}>
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white border-bottom">
                <h6 className="h6 mb-0">Edit Expense</h6>
              </div>
              <div className="card-body">
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3" controlId="title">
                    <Form.Label className="fw-bold">Expense Title</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter expense title"
                      isInvalid={!!errors.title}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.title}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="description">
                    <Form.Label className="fw-bold">Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Enter description (optional)"
                    />
                  </Form.Group>

                  <div className="row">
                    <Form.Group className="col-md-6 mb-3" controlId="amount">
                      <Form.Label className="fw-bold">Amount</Form.Label>
                      <Form.Control
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        placeholder="Enter amount"
                        min="0"
                        step="0.01"
                        isInvalid={!!errors.amount}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.amount}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="col-md-6 mb-3" controlId="on_date">
                      <Form.Label className="fw-bold">Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="on_date"
                        value={formData.on_date}
                        onChange={handleInputChange}
                        isInvalid={!!errors.on_date}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.on_date}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>

                  <Form.Group className="mb-3" controlId="paymentMethod">
                    <Form.Label className="fw-bold">Payment Method</Form.Label>
                    <Form.Select
                      name="payment_method"
                      value={formData.payment_method}
                      onChange={handleInputChange}
                      isInvalid={!!errors.payment_method}
                    >
                      <option value="">Select Payment Method</option>
                      {paymentMethods.map((method) => (
                        <option key={method.value} value={method.value}>
                          {method.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.payment_method}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="attachment">
                    <Form.Label className="fw-bold">Attachment (Optional)</Form.Label>
                    {formData.existingAttachment && (
                      <div className="mb-2">
                        <a
                          href={formData.existingAttachment}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary"
                        >
                          <Icon icon="bi:eye" className="me-1" />
                          View Current Attachment
                        </a>
                      </div>
                    )}
                    <Form.Control
                      type="file"
                      name="attachment"
                      onChange={handleFileChange}
                      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                    />
                  </Form.Group>

                  <div className="text-end">
                    <Button variant="primary" type="submit">
                      <Icon icon="simple-line-icons:check" className="me-2" />
                      Update
                    </Button>
                  </div>
                </Form>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </MasterLayout>
  );
};

export default EditExpenseLogPage;